import { useCallback, useEffect, useRef, useState } from 'react';

interface HoverIntentOptions {
  /** Delay before opening, so a pointer merely crossing the element does not trigger it. */
  openDelay?: number;
  /** Delay before closing, so the pointer can briefly leave (e.g. cross a gap) without collapsing. */
  closeDelay?: number;
  /** When false the hook never opens and immediately reports closed (e.g. on mobile). */
  enabled?: boolean;
}

/**
 * Hover with intent: opens after a short dwell and closes only after the pointer has stayed away
 * for `closeDelay`. Handlers use mouseEnter/mouseLeave (not mouseOver/mouseOut) so they fire once
 * for the whole subtree instead of on every child.
 */
export const useHoverIntent = ({ openDelay = 100, closeDelay = 350, enabled = true }: HoverIntentOptions = {}) => {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = undefined;
  };

  useEffect(() => clear, []);

  useEffect(() => {
    if (!enabled) {
      clear();
      setOpen(false);
    }
  }, [enabled]);

  const onMouseEnter = useCallback(() => {
    if (!enabled) return;
    clear();
    timer.current = setTimeout(() => setOpen(true), openDelay);
  }, [enabled, openDelay]);

  const onMouseLeave = useCallback(() => {
    clear();
    timer.current = setTimeout(() => setOpen(false), closeDelay);
  }, [closeDelay]);

  /** Close right now, skipping the delay — used when the user picks an item. */
  const close = useCallback(() => {
    clear();
    setOpen(false);
  }, []);

  /**
   * Open right now, skipping the dwell — for when the pointer is already inside and no mouseenter
   * will fire (e.g. the menu is collapsed by a control that sits within the menu itself).
   * Deliberately not gated on `enabled`: the caller may enable the hook in the same click, and the
   * `enabled && open` below still decides whether that open counts.
   */
  const openNow = useCallback(() => {
    clear();
    setOpen(true);
  }, []);

  return { open: enabled && open, close, openNow, handlers: { onMouseEnter, onMouseLeave } };
};

export default useHoverIntent;
